/**
 * Single source of truth for what each data-out integration can actually do.
 *
 * Drives:
 *  - The capability list rendered in the right side panel of the integration
 *    dialog.
 *  - The live test harness (`tests/integrations/live/**`) — the parity test
 *    asserts every `live` / `planned` capability has a corresponding code
 *    path in `CAPABILITY_IMPLEMENTATIONS`, so we cannot claim a capability
 *    without backing code.
 *  - The generated docs table (`docs/INTEGRATIONS.md`).
 *
 * Status semantics:
 *  - `live`         — wired all the way through a Brew route / product surface
 *                     and usable end to end today.
 *  - `planned`      — the provider supports it and we have the data-layer
 *                     function (or are adding one in Phase 5), but no
 *                     user-facing route ships yet.
 *  - `unsupported`  — the provider does not offer this capability at all.
 */

import { SUPPORTED_CLERK_EVENTS } from '@/lib/integrations/inbound/providers/clerk/event-types'
import { SUPPORTED_STRIPE_EVENTS } from '@/lib/integrations/oauth/stripe/event-types'

import type { IntegrationProvider } from './types'

const CLERK_EVENT_COUNT = SUPPORTED_CLERK_EVENTS.length
const STRIPE_EVENT_COUNT = SUPPORTED_STRIPE_EVENTS.length

export const INTEGRATION_CAPABILITY_IDS = [
  'export_template',
  'update_template',
  'list_templates',
  'delete_template',
  'send_transactional',
  'sync_contacts',
  'verified_senders',
  'import_design',
  // Data-in capabilities (Stripe, Typeform, future webhook providers):
  'event_triggers',
  'bulk_import',
  'lifecycle_emails',
] as const

export type IntegrationCapability = (typeof INTEGRATION_CAPABILITY_IDS)[number]

export type IntegrationCapabilityStatus = 'live' | 'planned' | 'unsupported'

export interface IntegrationCapabilityMeta {
  id: IntegrationCapability
  label: string
  description: string
  status: IntegrationCapabilityStatus
  /** Provider docs URL for this specific capability. */
  docsUrl?: string
}

const CAPABILITY_LABELS: Record<IntegrationCapability, string> = {
  export_template: 'Export template',
  update_template: 'Update template',
  list_templates: 'Import templates',
  delete_template: 'Delete template',
  send_transactional: 'Send email',
  sync_contacts: 'Sync contacts',
  verified_senders: 'Verified senders',
  import_design: 'Import design',
  event_triggers: 'Event triggers',
  bulk_import: 'Bulk customer import',
  lifecycle_emails: 'Lifecycle email templates',
}

const CAPABILITY_DESCRIPTIONS: Record<IntegrationCapability, string> = {
  export_template:
    'Push a new email template into the provider so campaigns and journeys can use it.',
  update_template:
    'Patch an existing template in place when Brew regenerates the email.',
  list_templates:
    'Browse templates that already live in the provider so you can edit them in Brew.',
  delete_template:
    'Remove a template from the provider when you no longer need it.',
  send_transactional:
    'Send a one-off transactional email through the provider directly from Brew.',
  sync_contacts: 'Look up or list people / contacts that live in the provider.',
  verified_senders:
    'Pick a verified sender identity at export time so the provider accepts the message.',
  import_design:
    'Import a Figma frame into Brew as a rendered, editable email draft.',
  event_triggers:
    'Trigger Brew automations when events happen on the connected account.',
  bulk_import:
    'One-click backfill of every customer / contact into the brand audience.',
  lifecycle_emails:
    'Pre-built starter automations for the most common provider lifecycles.',
}

function cap(
  id: IntegrationCapability,
  status: IntegrationCapabilityStatus,
  docsUrl?: string,
  overrides?: Partial<Pick<IntegrationCapabilityMeta, 'label' | 'description'>>
): IntegrationCapabilityMeta {
  return {
    id,
    label: overrides?.label ?? CAPABILITY_LABELS[id],
    description: overrides?.description ?? CAPABILITY_DESCRIPTIONS[id],
    status,
    ...(docsUrl ? { docsUrl } : {}),
  }
}

/**
 * Per-provider capability list. Order is the order the dialog renders.
 *
 * Cross-referenced against latest provider documentation in May 2026; any
 * change to a provider's API surface should land here in the same diff so the
 * dialog and tests stay accurate.
 */
export const INTEGRATION_CAPABILITIES: Record<
  IntegrationProvider,
  Array<IntegrationCapabilityMeta>
> = {
  loops: [
    cap(
      'export_template',
      'live',
      'https://loops.so/docs/api-reference/create-campaign',
      {
        description:
          'Create an editable draft campaign from supported Brew layouts. Images are uploaded to Loops; the export never sends email.',
      }
    ),
  ],
  framer: [
    cap(
      'event_triggers',
      'live',
      'https://www.framer.com/help/articles/framer-form-webhook-setup/',
      {
        description:
          'Each named form collects contacts and fires its own trigger.',
      }
    ),
    cap('sync_contacts', 'live', undefined, {
      description: 'Create or update contacts from signed form submissions.',
    }),
  ],
  braze: [
    cap(
      'export_template',
      'live',
      'https://www.braze.com/docs/api/endpoints/templates/email_templates/post_create_email_template'
    ),
    cap(
      'update_template',
      'planned',
      'https://www.braze.com/docs/api/endpoints/templates/email_templates/post_update_email_template'
    ),
    cap(
      'list_templates',
      'planned',
      'https://www.braze.com/docs/api/endpoints/templates/email_templates/get_list_email_templates'
    ),
  ],
  brevo: [
    cap(
      'export_template',
      'live',
      'https://developers.brevo.com/reference/create-smtp-template'
    ),
    cap(
      'update_template',
      'planned',
      'https://developers.brevo.com/reference/update-smtp-template'
    ),
    cap(
      'list_templates',
      'planned',
      'https://developers.brevo.com/reference/get-smtp-templates'
    ),
    cap(
      'send_transactional',
      'planned',
      'https://developers.brevo.com/reference/sendtransacemail'
    ),
    cap(
      'verified_senders',
      'live',
      'https://developers.brevo.com/reference/get-senders'
    ),
  ],
  customerio: [
    cap(
      'send_transactional',
      'planned',
      'https://customer.io/docs/journeys/transactional-api/'
    ),
    cap(
      'sync_contacts',
      'planned',
      'https://customer.io/docs/api/app/#operation/searchCustomers'
    ),
  ],
  hubspot: [
    cap(
      'export_template',
      'live',
      'https://developers.hubspot.com/docs/api-reference/legacy/cms/source-code/guide',
      {
        description:
          'Create a coded email template in Design Manager through the CMS v3 Source Code API.',
      }
    ),
  ],
  klaviyo: [
    cap(
      'export_template',
      'live',
      'https://developers.klaviyo.com/en/reference/create_template'
    ),
    cap(
      'update_template',
      'planned',
      'https://developers.klaviyo.com/en/reference/update_template'
    ),
    cap(
      'delete_template',
      'planned',
      'https://developers.klaviyo.com/en/reference/delete_template'
    ),
  ],
  mailchimp: [
    cap(
      'export_template',
      'live',
      'https://mailchimp.com/developer/marketing/api/templates/add-template/'
    ),
    cap(
      'update_template',
      'planned',
      'https://mailchimp.com/developer/marketing/api/templates/update-template/'
    ),
    cap(
      'delete_template',
      'planned',
      'https://mailchimp.com/developer/marketing/api/templates/delete-template/'
    ),
  ],
  iterable: [
    cap(
      'export_template',
      'live',
      'https://api.iterable.com/api/docs#templates_upsertEmail',
      {
        label: 'Upsert template',
        description:
          'Iterable upsert acts as both export and update — re-running with the same `clientTemplateId` patches the existing template.',
      }
    ),
    cap(
      'update_template',
      'live',
      'https://api.iterable.com/api/docs#templates_upsertEmail',
      {
        description:
          'Same upsert endpoint as export — `clientTemplateId` keeps it idempotent.',
      }
    ),
  ],
  postmark: [
    cap(
      'export_template',
      'live',
      'https://postmarkapp.com/developer/api/templates-api#create-template'
    ),
    cap(
      'update_template',
      'planned',
      'https://postmarkapp.com/developer/api/templates-api#edit-template'
    ),
    cap(
      'delete_template',
      'planned',
      'https://postmarkapp.com/developer/api/templates-api#delete-template'
    ),
  ],
  onesignal: [
    cap(
      'export_template',
      'live',
      'https://documentation.onesignal.com/reference/create-template'
    ),
    cap(
      'delete_template',
      'planned',
      'https://documentation.onesignal.com/reference/delete-template'
    ),
  ],
  mailgun: [
    cap(
      'export_template',
      'live',
      'https://documentation.mailgun.com/docs/mailgun/api-reference/send/mailgun/domain-templates/post-v3--domain-name--templates'
    ),
    cap(
      'update_template',
      'planned',
      'https://documentation.mailgun.com/docs/mailgun/api-reference/send/mailgun/domain-templates/post-v3--domain-name--templates--template-name--versions'
    ),
    cap(
      'send_transactional',
      'planned',
      'https://documentation.mailgun.com/docs/mailgun/api-reference/send/mailgun/messages/post-v3--domain-name--messages'
    ),
  ],
  mailjet: [
    cap(
      'export_template',
      'live',
      'https://dev.mailjet.com/email/reference/templates/'
    ),
    cap(
      'update_template',
      'planned',
      'https://dev.mailjet.com/email/reference/templates/'
    ),
    cap(
      'delete_template',
      'planned',
      'https://dev.mailjet.com/email/reference/templates/'
    ),
  ],
  sendgrid: [
    cap(
      'export_template',
      'live',
      'https://docs.sendgrid.com/api-reference/transactional-templates/create-a-transactional-template'
    ),
    cap(
      'update_template',
      'planned',
      'https://docs.sendgrid.com/api-reference/transactional-templates-versions/create-a-new-transactional-template-version',
      {
        description:
          'Add a new version to an existing dynamic template (SendGrid versions templates instead of patching content in place).',
      }
    ),
    cap(
      'delete_template',
      'planned',
      'https://docs.sendgrid.com/api-reference/transactional-templates/delete-a-template'
    ),
  ],
  // Data-in only / non-ESP integrations. Stripe carries its own data-in
  // capability set; Figma / Slack / Typeform have no surfaced data-out
  // capabilities yet but the explicit empty arrays keep the parity test
  // aware of every provider.
  clerk: [
    cap(
      'event_triggers',
      'live',
      'https://clerk.com/docs/integrations/webhooks/overview',
      {
        description: `Trigger Brew automations from ${CLERK_EVENT_COUNT} Clerk webhook events across user, organization, billing, and waitlist categories.`,
      }
    ),
  ],
  figma: [
    cap(
      'import_design',
      'live',
      'https://developers.figma.com/docs/rest-api/file-node-types/',
      {
        description:
          'Import a Figma frame into Brew as a rendered, editable email draft.',
      }
    ),
  ],
  slack: [],
  stripe: [
    cap('event_triggers', 'live', 'https://docs.stripe.com/webhooks', {
      description: `Trigger Brew automations from ${STRIPE_EVENT_COUNT} Stripe events across subscriptions, invoices, checkouts, quotes, charges, refunds, and payment intents.`,
    }),
    cap('sync_contacts', 'live', 'https://docs.stripe.com/api/customers', {
      description:
        'Sync Stripe customers into the brand audience with `stripe_*` custom fields available as filterable attributes.',
    }),
    cap('bulk_import', 'live', 'https://docs.stripe.com/api/customers/list', {
      description:
        'One-click backfill of every Stripe customer into the active brand. Resumable, durable via Vercel Workflow.',
    }),
    cap(
      'lifecycle_emails',
      'live',
      'https://docs.brew.new/integrations/import/stripe',
      {
        description:
          'Starter templates for welcome, trial-end, failed-payment recovery, and win-back.',
      }
    ),
  ],
  typeform: [],
  stytch: [
    cap(
      'event_triggers',
      'live',
      'https://stytch.com/docs/workspace-management/webhooks',
      {
        description:
          'Trigger Brew automations from direct Stytch Consumer user and B2B member events with a known recipient. Organization creation, Dashboard events, and SCIM events do not start automations.',
      }
    ),
  ],
  supabase: [
    cap(
      'event_triggers',
      'live',
      'https://supabase.com/docs/guides/database/webhooks',
      {
        description:
          'Trigger Brew automations from Supabase auth.users database webhooks, including derived events for email confirmation, password change, and sign-in.',
      }
    ),
  ],
  workos: [
    cap('event_triggers', 'live', 'https://workos.com/docs/events', {
      description:
        'Trigger Brew automations from WorkOS user, authentication, organization, directory sync, and connection events.',
    }),
  ],
  shopify: [
    cap(
      'event_triggers',
      'live',
      'https://shopify.dev/docs/apps/build/webhooks',
      {
        description:
          'Trigger Brew automations from Shopify customer, order, checkout, and fulfillment events. Powers welcome, receipt, abandoned-checkout, and shipping flows.',
      }
    ),
  ],
  revenuecat: [
    cap(
      'event_triggers',
      'live',
      'https://www.revenuecat.com/docs/integrations/webhooks',
      {
        description:
          'Trigger Brew automations from RevenueCat in-app purchase, subscription, and billing events across Apple, Google, Amazon, Stripe, and Web Billing.',
      }
    ),
  ],
}

export function getCapabilitiesForProvider(
  provider: IntegrationProvider
): Array<IntegrationCapabilityMeta> {
  return INTEGRATION_CAPABILITIES[provider] ?? []
}

/**
 * Code-path receipts for every `live` / `planned` capability. The parity test
 * asserts each entry resolves to a real exported function — preventing the
 * catalog from drifting away from the data layer.
 *
 * Format: `<module path>#<exported function name>`. The path is relative to
 * the repo root and corresponds to a real file under `lib/integrations/**`.
 */
export const CAPABILITY_IMPLEMENTATIONS: Record<
  IntegrationProvider,
  Partial<Record<IntegrationCapability, string>>
> = {
  loops: {
    export_template:
      'lib/integrations/api/loops/data-out/templates#createCampaignTemplate',
  },
  framer: {
    event_triggers:
      'lib/integrations/inbound/process-trigger-instance#processTriggerInstance',
    sync_contacts:
      'lib/triggers/sync-trigger-payload-contact#syncTriggerPayloadContact',
  },
  braze: {
    export_template:
      'lib/integrations/api/braze/data-out/templates#createEmailTemplate',
    update_template:
      'lib/integrations/api/braze/data-out/templates#updateEmailTemplate',
    list_templates:
      'lib/integrations/api/braze/data-out/templates#listEmailTemplates',
  },
  brevo: {
    export_template:
      'lib/integrations/api/brevo/data-out/templates#createTemplate',
    update_template:
      'lib/integrations/api/brevo/data-out/templates#updateTemplate',
    list_templates:
      'lib/integrations/api/brevo/data-out/templates#listTemplates',
    send_transactional:
      'lib/integrations/api/brevo/data-out/templates#sendTransactionalEmail',
    verified_senders:
      'lib/integrations/api/brevo/data-out/templates#getActiveSenders',
  },
  customerio: {
    send_transactional:
      'lib/integrations/api/customerio/data-out/emails#sendEmailWithBody',
    sync_contacts:
      'lib/integrations/api/customerio/data-out/emails#searchPeople',
  },
  hubspot: {
    export_template:
      'lib/integrations/oauth/hubspot/data-out/emails#createTemplate',
  },
  klaviyo: {
    export_template:
      'lib/integrations/oauth/klaviyo/data-out/templates#createTemplate',
    update_template:
      'lib/integrations/oauth/klaviyo/data-out/templates#updateTemplate',
    delete_template:
      'lib/integrations/oauth/klaviyo/data-out/templates#deleteTemplate',
  },
  mailchimp: {
    export_template:
      'lib/integrations/oauth/mailchimp/data-out/templates#createTemplate',
    update_template:
      'lib/integrations/oauth/mailchimp/data-out/templates#updateTemplate',
    delete_template:
      'lib/integrations/oauth/mailchimp/data-out/templates#deleteTemplate',
  },
  iterable: {
    export_template:
      'lib/integrations/api/iterable/data-out/templates#upsertEmailTemplate',
    update_template:
      'lib/integrations/api/iterable/data-out/templates#upsertEmailTemplate',
  },
  postmark: {
    export_template:
      'lib/integrations/api/postmark/data-out/templates#createTemplate',
    update_template:
      'lib/integrations/api/postmark/data-out/templates#updateTemplate',
    delete_template:
      'lib/integrations/api/postmark/data-out/templates#deleteTemplate',
  },
  onesignal: {
    export_template:
      'lib/integrations/api/onesignal/data-out/templates#createTemplate',
    delete_template:
      'lib/integrations/api/onesignal/data-out/templates#deleteTemplate',
  },
  mailgun: {
    export_template:
      'lib/integrations/api/mailgun/data-out/templates#createTemplate',
    update_template:
      'lib/integrations/api/mailgun/data-out/templates#createTemplateVersion',
    send_transactional:
      'lib/integrations/api/mailgun/data-out/templates#sendMessage',
  },
  mailjet: {
    export_template:
      'lib/integrations/api/mailjet/data-out/templates#createTemplate',
    update_template:
      'lib/integrations/api/mailjet/data-out/templates#updateTemplateContent',
    delete_template:
      'lib/integrations/api/mailjet/data-out/templates#deleteTemplate',
  },
  sendgrid: {
    export_template:
      'lib/integrations/api/sendgrid/data-out/templates#createTemplate',
    update_template:
      'lib/integrations/api/sendgrid/data-out/templates#createTemplateVersion',
    delete_template:
      'lib/integrations/api/sendgrid/data-out/templates#deleteTemplate',
  },
  clerk: {
    event_triggers:
      'lib/integrations/inbound/process-trigger-instance#processTriggerInstance',
  },
  figma: {
    import_design:
      'lib/integrations/oauth/figma/data-in/service#getFigmaPreviewBundle',
  },
  slack: {},
  stripe: {
    event_triggers:
      'lib/integrations/oauth/stripe/data-in/processor#processStripeEvent',
    sync_contacts:
      'lib/integrations/oauth/stripe/data-in/contact-sync#syncStripeContactToOrg',
    bulk_import:
      'lib/integrations/oauth/stripe/data-in/bulk-import.workflow#runStripeBulkImport',
    lifecycle_emails:
      'lib/integrations/oauth/stripe/templates/stripe-automation-templates#buildStripeTemplateLaunchHref',
  },
  typeform: {},
  stytch: {
    event_triggers:
      'lib/integrations/inbound/process-trigger-instance#processTriggerInstance',
  },
  supabase: {
    event_triggers:
      'lib/integrations/inbound/process-trigger-instance#processTriggerInstance',
  },
  workos: {
    event_triggers:
      'lib/integrations/inbound/process-trigger-instance#processTriggerInstance',
  },
  shopify: {
    event_triggers:
      'lib/integrations/inbound/process-trigger-instance#processTriggerInstance',
  },
  revenuecat: {
    event_triggers:
      'lib/integrations/inbound/process-trigger-instance#processTriggerInstance',
  },
}
